// @spec BCP-STR-026
// Exercise list page – view and manage tabletop exercises
'use client';

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Calendar, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useExercisesList } from '@/features/bcp/api/exercises';
import { ExerciseStatusBadge } from '@/features/bcp/components/ExerciseStatusBadge';
import type { Exercise } from '@/features/bcp/types/exercises';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

// ---------------------------------------------------------------------------
// URL search params
// ---------------------------------------------------------------------------

const exerciseListSearchSchema = z.object({
  page: z.coerce.number().optional().default(1),
  size: z.coerce.number().optional().default(20),
  status: z.enum(['planned', 'in_progress', 'completed', 'cancelled']).optional(),
  search: z.string().optional(),
  sort_by: z
    .enum(['scheduled_date', 'name', 'status', 'created_at'])
    .optional()
    .default('scheduled_date'),
  sort_order: z.enum(['asc', 'desc']).optional().default('desc'),
});

type ExerciseListSearch = z.infer<typeof exerciseListSearchSchema>;

export const Route = createFileRoute('/_authenticated/bcp/exercises/')({
  validateSearch: exerciseListSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.tabletopExercises'),
  }),
  component: ExercisesListPage,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ExercisesListPage() {
  const { t } = useTranslation('bcp');
  const navigate = useNavigate();
  const search: ExerciseListSearch = useSearch({ from: '/_authenticated/bcp/exercises/' });
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  const activeOrg = organizations.find((org) => org.isActive);

  const {
    data: exercisesData,
    isLoading: exercisesLoading,
    isFetching: exercisesFetching,
  } = useExercisesList(
    {
      page: search.page,
      size: search.size,
      status: search.status,
      search: search.search,
      sort_by: search.sort_by,
      sort_order: search.sort_order,
    },
    { enabled: Boolean(activeOrg) }
  );

  // Handle pagination
  const handlePageChange = React.useCallback(
    (page: number) => {
      void navigate({
        search: { ...search, page } as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Format date for display
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No org selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('exercises.noOrgTitle')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('exercises.noOrgDescription')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('exercises.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const totalPages = exercisesData?.pages ?? 1;
  const currentPage = exercisesData?.page ?? 1;
  const exercises: Exercise[] = exercisesData?.items ?? [];

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-semibold text-foreground">{t('exercises.pageTitle')}</h1>
              <p className="mt-1 text-muted-foreground">
                {t('exercises.pageDescription', { orgName: activeOrg.name })}
              </p>
            </div>
          </div>
          <Button onClick={() => void navigate({ to: '/bcp/exercises/new' })}>
            <Plus className="mr-1 h-4 w-4" />
            {t('exercises.newExercise')}
          </Button>
        </header>

        {/* Content */}
        {exercisesLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : exercises.length === 0 ? (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">{t('exercises.noExercisesTitle')}</h3>
            <p className="mt-2 text-muted-foreground">
              {t('exercises.noExercisesDescription')}
            </p>
            <Button className="mt-4" onClick={() => void navigate({ to: '/bcp/exercises/new' })}>
              <Plus className="mr-1 h-4 w-4" />
              {t('exercises.createExercise')}
            </Button>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('exercises.tableHeaderName')}</TableHead>
                    <TableHead>{t('exercises.tableHeaderStatus')}</TableHead>
                    <TableHead>{t('exercises.tableHeaderScheduledDate')}</TableHead>
                    <TableHead>{t('exercises.tableHeaderPlansTested')}</TableHead>
                    <TableHead>{t('exercises.tableHeaderObjectives')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exercises.map((exercise) => (
                    <TableRow
                      key={exercise.id}
                      className="cursor-pointer"
                      onClick={() =>
                        void navigate({
                          to: '/bcp/exercises/$exerciseId' as never,
                          params: { exerciseId: exercise.id } as never,
                        })
                      }
                    >
                      <TableCell className="font-medium">{exercise.name}</TableCell>
                      <TableCell>
                        <ExerciseStatusBadge status={exercise.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(exercise.scheduledDate)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {exercise.testedPlans.length > 0
                          ? exercise.testedPlans.map((p) => p.name).join(', ')
                          : t('exercises.planCount', { count: exercise.testedPlanIds.length })}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {exercise.objectives.length}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  {t('exercises.paginationInfo', { current: currentPage, total: totalPages, count: exercisesData?.total ?? 0 })}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePageChange(currentPage - 1);
                    }}
                    disabled={currentPage <= 1 || exercisesFetching}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('exercises.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePageChange(currentPage + 1);
                    }}
                    disabled={currentPage >= totalPages || exercisesFetching}
                  >
                    {t('exercises.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
